import CryptoJS from 'crypto-js';

/**
 * Encrypts a given text using a custom key.
 * Uses AES-256-CBC with a random IV.
 * The key is hashed using SHA-256 to ensure it is exactly 32 bytes.
 * Matches Node.js backend crypto.js behavior.
 * 
 * @param {string} text - The plaintext to encrypt.
 * @param {string} key - The encryption key.
 * @returns {string} The encrypted text formatted as iv:encryptedData (hex encoded).
 */
export function encrypt(text, key) {
  if (!text || !key) {
    throw new Error('Both text and key are required for encryption');
  }

  // Derive a 32-byte key from the provided key using SHA-256
  const derivedKey = CryptoJS.SHA256(key);

  // Generate a random 16-byte (128-bit) IV
  const iv = CryptoJS.lib.WordArray.random(16);

  // Encrypt using AES-CBC with PKCS7 padding
  const encrypted = CryptoJS.AES.encrypt(text, derivedKey, {
    iv: iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7
  });

  const ivHex = iv.toString(CryptoJS.enc.Hex);
  const encryptedHex = encrypted.ciphertext.toString(CryptoJS.enc.Hex);

  // Return the IV and the encrypted text joined by a colon
  return `${ivHex}:${encryptedHex}`;
}

/**
 * Decrypts a given encrypted text using the custom key.
 * Matches Node.js backend crypto.js behavior.
 * 
 * @param {string} encryptedText - The encrypted text in the format iv:encryptedData (hex encoded).
 * @param {string} key - The decryption key.
 * @returns {string} The decrypted plaintext.
 */
export function decrypt(encryptedText, key) {
  if (!encryptedText || !key) {
    throw new Error('Both encryptedText and key are required for decryption');
  }

  // Split the iv and encrypted data
  const parts = encryptedText.split(':');
  if (parts.length !== 2) {
    throw new Error('Invalid encrypted text format');
  }

  const iv = CryptoJS.enc.Hex.parse(parts[0]);
  const encryptedHex = parts[1];

  // Derive the 32-byte key from the provided key using SHA-256
  const derivedKey = CryptoJS.SHA256(key);

  // Create CipherParams from hex ciphertext
  const cipherParams = CryptoJS.lib.CipherParams.create({
    ciphertext: CryptoJS.enc.Hex.parse(encryptedHex)
  });

  // Decrypt using AES-CBC with PKCS7 padding
  const decrypted = CryptoJS.AES.decrypt(cipherParams, derivedKey, {
    iv: iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7
  });

  return decrypted.toString(CryptoJS.enc.Utf8);
}
